import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import type {
  GaussianSplatExtraLoader,
  GSplatExtraLoaderCapability,
  GSplatExtraLoaderResourceTarget,
} from '@/plugins/gsplat/types/PluginTypes'
import type { GSplatCapability, GSplatCapabilityName } from '@/shared/types/gsplatCapability'

const RESOURCE_CONTRACTS: Readonly<
  Record<
    GSplatExtraLoaderResourceTarget,
    Readonly<{
      format: GSplatExtraLoaderCapability['format']
      streaming: GSplatExtraLoaderCapability['streaming']
    }>
  >
> = Object.freeze({
  GSplatResource: Object.freeze({ format: 'uncompressed', streaming: 'static' }),
  GSplatCompressedResource: Object.freeze({ format: 'compressed', streaming: 'static' }),
  GSplatSogResource: Object.freeze({ format: 'sog', streaming: 'static' }),
  GSplatOctreeResource: Object.freeze({ format: 'mixed', streaming: 'lod' }),
})

const FORMAT_CAPABILITIES: Readonly<
  Record<GSplatExtraLoaderCapability['format'], readonly GSplatCapabilityName[]>
> = Object.freeze({
  uncompressed: Object.freeze(['unified-format-reader', 'uncompressed-reader'] as const),
  compressed: Object.freeze(['unified-format-reader', 'compressed-reader'] as const),
  sog: Object.freeze(['unified-format-reader', 'sog-reader'] as const),
  mixed: Object.freeze([
    'unified-format-reader',
    'uncompressed-reader',
    'compressed-reader',
    'sog-reader',
  ] as const),
})

/** WebGPU 自定义 loader 的目标 Resource/Format/streaming 声明无效或不可用。 */
export class UnsupportedGSplatExtraLoaderCapabilityError extends Error {
  readonly code = 'GSPLAT_EXTRA_LOADER_CAPABILITY_UNSUPPORTED'

  constructor(
    readonly loaderFormat: string,
    readonly backend: GraphicsBackendKind,
    readonly reason: string,
    readonly capability?: GSplatExtraLoaderCapability
  ) {
    super(`3DGS 自定义 loader "${loaderFormat}" 不支持 ${backend}：${reason}`)
    this.name = new.target.name
  }
}

function assertWebGPUCapability(
  loader: GaussianSplatExtraLoader,
  capabilities: Pick<GSplatCapability, 'supported'>
): void {
  const declaration = loader.gsplatCapability
  if (!declaration) {
    throw new UnsupportedGSplatExtraLoaderCapabilityError(
      loader.format,
      'webgpu',
      '缺少 gsplatCapability 的 Resource/Format/streaming 声明'
    )
  }

  const expected = RESOURCE_CONTRACTS[declaration.resource]
  if (
    !expected ||
    expected.format !== declaration.format ||
    expected.streaming !== declaration.streaming
  ) {
    throw new UnsupportedGSplatExtraLoaderCapabilityError(
      loader.format,
      'webgpu',
      `声明组合无效，${declaration.resource} 必须使用 ${expected?.format ?? '未知'} / ${expected?.streaming ?? '未知'}`,
      declaration
    )
  }

  const required = [...FORMAT_CAPABILITIES[declaration.format]]
  if (declaration.streaming === 'lod') required.push('lod-streaming')
  const missing = required.filter((name) => !capabilities.supported.includes(name))
  if (missing.length > 0) {
    throw new UnsupportedGSplatExtraLoaderCapabilityError(
      loader.format,
      'webgpu',
      `当前 GSplatSystem 缺少能力：${missing.join(', ')}`,
      declaration
    )
  }
}

/**
 * 在自定义 loader 的副作用边界增加 WebGPU capability preflight。
 *
 * WebGL 必须返回原 loader，保持已有自定义 loader 的对象身份与行为；WebGPU 只在真正
 * 命中并调用 load 时校验，未命中的 loader 不影响其他格式。
 */
export function guardGSplatExtraLoaderCapability(
  loader: GaussianSplatExtraLoader,
  backend: GraphicsBackendKind,
  capabilities: Pick<GSplatCapability, 'supported'>
): GaussianSplatExtraLoader {
  if (backend === 'webgl2') return loader

  return {
    format: loader.format,
    detectPriority: loader.detectPriority,
    gsplatCapability: loader.gsplatCapability,
    canLoad: loader.canLoad
      ? (request) => loader.canLoad?.call(loader, request) ?? false
      : undefined,
    load: async (request, context) => {
      assertWebGPUCapability(loader, capabilities)
      return loader.load.call(loader, request, context)
    },
  }
}
